import React, { useEffect, useMemo, useState } from "react";

import axios from "axios";

import "./css/OfflineUsage.css";

import useSelect from "../../../../hooks/useSelect";
import usePagination from "../../../../hooks/usePagination";

import FilterModal from "./component/FilterModal";
import Pagination from "./component/Pagination";

import FilterIcon from "../../../../images/filter.png";
import SortIcon from "../../../../images/sort.png";
import ExportIcon from "../../../../images/export.svg";

import { exportToExcel } from "../../../../utils/excel";
import {
  getDate,
  formatTime,
  formatDateTime,
} from "../../../../utils/datetime";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../../Auth/Auth";

const today = `${getDate(new Date())}T23:59:59`;

function getFilterSummary({ results, user, grade, fromDate, toDate }) {
  const parts = [];

  // No results or how many
  if (results === 0) {
    parts.push("0 results");
  } else {
    parts.push(`<b>${results}</b> result${results > 1 ? "s" : ""}`);
  }

  // Filtered by teacher
  if (user && user !== "All") {
    parts.push(`for <b>${user}</b>`);
  }

  // Filtered by grade
  if (grade && grade !== "All") {
    parts.push(`in grade <b>${grade.split("-")[0]}</b>`);
  }

  if (fromDate && toDate) {
    const isSame = fromDate === toDate;

    const diffFromToday = Math.floor(
      (new Date() - fromDate) / (1000 * 60 * 60 * 24),
    );
    const isLast10Days = diffFromToday <= 10 && toDate >= fromDate;

    if (isSame) {
      parts.push(`on <b>${fromDate}</b>`);
    } else if (isLast10Days) {
      parts.push("in the last 10 days");
    } else {
      parts.push(`from <b>${fromDate}</b> to <b>${toDate.split("T")[0] || ""}</b>`);
    }
  } else if (fromDate) {
    parts.push(`from <b>${fromDate}</b>`);
  } else if (toDate) {
    parts.push(`up to <b>${toDate.split("T")[0] || ""}</b>`);
  }

  return parts.join(" ");
}

function getGradeName(section) {
  return section ? section.split("-")[0] : "";
}

const sectionOrder = [
  "NURSERY",
  "LKG",
  "UKG",
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "XII",
];

export function sortGrades(grades, key = "name") {
  const orderMap = new Map(sectionOrder.map((grade, index) => [grade, index]));

  return grades.sort(
    (a, b) =>
      (orderMap.get(a[key]) || Infinity) - (orderMap.get(b[key]) || Infinity),
  );
}

function SortTH(props) {
  return (
    <th onClick={props.sort} className="sort-th">
      <div>
        <span>{props.children}</span>
        <img className={props.order || "asc"} src={SortIcon} alt="sort" />
      </div>
    </th>
  );
}

function UsageDashboard() {
  const { schoolId } = useParams();
  const { readCookie } = useAuth();
  const [pagevisits, setPageVisits] = useState([]);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [filters, setFilters] = useState({
    "visitOn[gte]": getDate(new Date(Date.now() - 31 * 24 * 60 * 60 * 1000)),
    "visitOn[lte]": today,
    user: "",
    section: "",
    sort: "visitOn",
    mode: "all",
  });
  const [exportFilters, setExportFilters] = useState({
    "visitOn[gte]": getDate(new Date(Date.now() - 31 * 24 * 60 * 60 * 1000)),
    "visitOn[lte]": today,
    user: "",
    section: "",
    sort: "visitOn",
    mode: "all",
  });
  const teacherSelect = useSelect([]);
  const gradeSelect = useSelect([]);
  const modeSelect = useSelect([
    { name: "All", value: "all" },
    { name: "Online", value: "online" },
    { name: "Offline", value: "offline" },
  ]);
  const { setTotalRows, limit, page, changePage } = usePagination(20);
  const [pageData, setPageData] = useState([]);
  const [sortOrder, setSortOrder] = useState({});

  function toggleSortOrder(keyName) {
    const currentOrder = sortOrder[keyName];
    const newOrder = currentOrder === "desc" ? "asc" : "desc";
    setSortOrder({
      [keyName]: newOrder,
    });
  }

  function resetExportFilter() {
    setExportFilters({
      "visitOn[gte]": getDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
      "visitOn[lte]": today,
      user: "",
      section: "",
      sort: "visitOn",
      mode: "all",
    });
    teacherSelect.reset();
    gradeSelect.reset();
  }

  const fetchVisitors = async (query) => {
    setLoading(true);
    const authToken = readCookie("token");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/schools/${schoolId}/pagevisits?${query}`,
        {
          headers: {
            token: `${authToken}`,
          },
        },
      );
      if (response.data.status === "success") {
        const { pageVisits, offlinePageVisits } = response.data.data;
        const pagevisitsData = [...pageVisits, ...offlinePageVisits].sort(
          (a, b) => new Date(b.visitOn) - new Date(a.visitOn),
        );

        if (filter === "export") {
          exportToExcel({
            data: pagevisitsData.map((page) => {
              const [date, time] = formatDateTime(page.visitOn);
              return {
                Class: page.section,
                Teacher: page?.user?.name || "",
                Subject: page.subject,
                Chapter: page.chapter,
                Topic: page.topic,
                Module: page.module,
                Date: date,
                Time: time,
                "Time Spent": formatTime(page.duration),
                Mode: page.syncedAt ? "Offline" : "Online",
              };
            }),
          });
          resetExportFilter();
        } else {
          setPageVisits(pagevisitsData);
          setTotalRows(pagevisitsData.length);
          const totalTimeSpt = pagevisitsData.reduce(
            (sum, item) => sum + (item.duration || 0),
            0,
          );
          setTotalTimeSpent(totalTimeSpt);
        }
      }
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchTeachers = async () => {
    const authToken = readCookie("token");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/staffs?school=${schoolId}&export=true`,
        {
          headers: {
            token: `${authToken}`,
          },
        },
      );
      if (response.data.status === "success") {
        const options = (response.data?.data?.staffs || []).map((teacher) => {
          return {
            name: teacher.name,
            value: teacher._id,
            content: teacher.sections,
          };
        });
        teacherSelect.setOptions([{ name: "All", value: "" }, ...options]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchGrades = async () => {
    const authToken = localStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/grades?school=${schoolId}`,
        {
          headers: {
            token: authToken,
          },
        },
      );
      if (response.data.status === "success") {
        const options = response.data.data.grades.map(({ grade }) => {
          return {
            name: grade,
            value: `${grade}-A`,
          };
        });
        gradeSelect.setOptions([
          { name: "All", value: "" },
          ...sortGrades(options),
        ]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  async function filterPageVisit(filterQuery) {
    filterQuery["user"] = teacherSelect.selectedOption?.value || "";
    filterQuery["section"] = gradeSelect.selectedOption?.value || "";
    filterQuery["mode"] = modeSelect.selectedOption?.value || "all";
    for (let key in filterQuery) {
      if (!filterQuery[key]) delete filterQuery[key];
    }
    await fetchVisitors(new URLSearchParams(filterQuery).toString());
  }

  useEffect(() => {
    (async () => {
      await filterPageVisit(filters);
      await fetchTeachers();
      await fetchGrades();
    })();
  }, []);

  const sortedData = useMemo(() => {
    const sortKeys = Object.keys(sortOrder);

    return [...pagevisits].sort((a, b) => {
      for (const key of sortKeys) {
        const order = sortOrder[key];
        const aVal = a[key];
        const bVal = b[key];

        if (aVal === bVal) continue;

        // Special case for 'section' sorting
        if (key === "section") {
          const aGrade = getGradeName(aVal);
          const bGrade = getGradeName(bVal);

          const aIndex = sectionOrder.indexOf(aGrade);
          const bIndex = sectionOrder.indexOf(bGrade);

          return order === "asc" ? aIndex - bIndex : bIndex - aIndex;
        }

        if (typeof aVal === "number" && typeof bVal === "number") {
          return order === "asc" ? aVal - bVal : bVal - aVal;
        }

        const comparison = String(aVal).localeCompare(String(bVal));
        return order === "asc" ? comparison : -comparison;
      }

      return 0; // all keys equal
    });
  }, [pagevisits, sortOrder]);

  useEffect(() => {
    (async () => {
      setPageData([]);
      const newRows = [...sortedData].splice(page.current * limit, limit);
      setPageData(newRows);
    })();
  }, [page, sortedData]);

  const statement = useMemo(() => {
    if (filter === "export") return;
    const teacher = (teacherSelect.options || []).find(
      (opt) => `${opt.value}` === filters.user,
    );
    return getFilterSummary({
      results: pagevisits.length,
      user: teacher ? teacher?.name : undefined,
      grade: filters["section"],
      fromDate: filters["visitOn[gte]"],
      toDate: filters["visitOn[lte]"],
    });
  }, [pagevisits, filter]);

  return (
    <>
      <div className="container">
        <div className="cards">
          <div className="card">
            <h1>{pagevisits.length}</h1>
            <h2>Page Visits</h2>
          </div>
          <div className="card">
            <h1>{formatTime(totalTimeSpent)}</h1>
            <h2>Time Spent</h2>
          </div>
        </div>
        <div className="tool">
          {loading ? (
            <div
              className="loader"
              style={{ width: "20px", height: "20px", margin: "auto 0" }}
            ></div>
          ) : (
            <></>
          )}
          <button className="filter" onClick={() => setFilter("normal")}>
            <img src={FilterIcon} alt="filter" />
            <span>Filter</span>
          </button>
          <button className="filter" onClick={() => setFilter("export")}>
            <img src={ExportIcon} alt="export" />
            <span>Export</span>
          </button>
          &nbsp;
          <Pagination changePage={changePage} page={page} />
        </div>
        <span
          style={{ marginBottom: "8px", fontSize: "0.9rem" }}
          dangerouslySetInnerHTML={{ __html: statement }}
        ></span>
        <div className="">
          {pagevisits.length ? (
            <div className="table-container">
              <table className="visit-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <SortTH
                      order={sortOrder["user.name"]}
                      sort={() => toggleSortOrder("user.name")}
                    >
                      Teacher
                    </SortTH>
                    <SortTH
                      order={sortOrder["section"]}
                      sort={() => toggleSortOrder("section")}
                    >
                      Grade
                    </SortTH>
                    <SortTH
                      order={sortOrder["subject"]}
                      sort={() => toggleSortOrder("subject")}
                    >
                      Subject
                    </SortTH>
                    <SortTH
                      order={sortOrder["chapter"]}
                      sort={() => toggleSortOrder("chapter")}
                    >
                      Chapter
                    </SortTH>
                    <SortTH
                      order={sortOrder["topic"]}
                      sort={() => toggleSortOrder("topic")}
                    >
                      Topic
                    </SortTH>
                    <SortTH
                      order={sortOrder["module"]}
                      sort={() => toggleSortOrder("module")}
                    >
                      Module
                    </SortTH>
                    <th>Mode</th>
                    <SortTH
                      order={sortOrder["visitOn"]}
                      sort={() => toggleSortOrder("visitOn")}
                    >
                      Visit Date
                    </SortTH>
                    <th>Visit Time</th>
                    <SortTH
                      order={sortOrder["duration"]}
                      sort={() => toggleSortOrder("duration")}
                    >
                      Time Spent
                    </SortTH>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map((item, index) => {
                    const [date, time] = formatDateTime(item.visitOn);
                    const mode = item.syncedAt ? "offline" : "online";
                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item?.user?.name}</td>
                        <td>{getGradeName(item?.section)}</td>
                        <td>{item.subject}</td>
                        <td>{item.chapter}</td>
                        <td>{item.topic}</td>
                        <td>{item.module}</td>
                        <td>
                          <span
                            style={{
                              backgroundColor:
                                mode === "offline" ? "#FFEAEA" : "#E6F4EA",
                              color: mode === "offline" ? "#FF6B6B" : "#4E944F",
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontWeight: "bolder",
                              fontSize: "0.8rem",
                            }}
                          >
                            {mode}
                          </span>
                        </td>
                        <td>{date}</td>
                        <td>{time}</td>
                        <td>{formatTime(item.duration)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <br />
            </div>
          ) : (
            <div
              style={{
                width: "fit-content",
                fontSize: "30px",
                color: "#D1D1D1",
                margin: "50px auto",
              }}
            >
              No Page Visits
            </div>
          )}
        </div>
      </div>
      {filter && (
        <FilterModal
          type={filter}
          onClose={() => setFilter("")}
          filter={filterPageVisit}
          filters={filter === "export" ? exportFilters : filters}
          setFilters={filter === "export" ? setExportFilters : setFilters}
          teacherSelect={teacherSelect}
          gradeSelect={gradeSelect}
          modeSelect={modeSelect}
        />
      )}
    </>
  );
}

export default UsageDashboard;
